import { z } from "zod";

// D70 — Prop-Typsystem (engine- und domänen-NEUTRAL). Beschreibt die konfigurierbaren Werte
// eines Behaviors. EINE Deklaration speist DREI Konsumenten (D70): das Editor-Details-Formular
// (gerendert über die bestehende M7-Zod-Settings-Form-Engine — KEIN zweites Formular-System),
// die Blockly-Value-Block-Formen (D71) und die Runtime-Init-Parameter.
//
// Domänen-neutral: KEIN Grid, KEIN festes x/y (Neutralitäts-Prinzip §1.7 der Editor_PRD) —
// Platformer-Spezifika leben im TDoc des Editors (D75), nicht hier.

/** Rekursiver Prop-Typ (das `list`-Element verweist wieder auf einen PropType). */
export type PropType =
  | { kind: "number"; default?: number; min?: number; max?: number; step?: number; unit?: string }
  | { kind: "int"; default?: number; min?: number; max?: number }
  | { kind: "bool"; default?: boolean }
  | { kind: "string"; default?: string; multiline?: boolean }
  | { kind: "enum"; options: string[]; default?: string }
  | { kind: "vec2"; default?: [number, number] }
  | { kind: "color"; default?: string } // #rrggbb(aa)
  | { kind: "asset-ref" } // verweist auf eine Placeable-Katalog-id (PlaceableDescriptor.id); Kind-Name bleibt stabil (D82)
  | { kind: "entity-ref" } // verweist auf eine Level-Instanz-iid (TDoc, D75)
  | { kind: "range"; min: number; max: number; default?: number }
  | { kind: "list"; of: PropType };

/** Zod-SSOT des Prop-Typs (rekursiv via z.lazy, da `list.of` wieder ein PropType ist). */
export const PropType: z.ZodType<PropType> = z.lazy(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("number"),
      default: z.number().optional(),
      min: z.number().optional(),
      max: z.number().optional(),
      step: z.number().optional(),
      unit: z.string().optional(),
    }),
    z.object({
      kind: z.literal("int"),
      default: z.number().int().optional(),
      min: z.number().int().optional(),
      max: z.number().int().optional(),
    }),
    z.object({ kind: z.literal("bool"), default: z.boolean().optional() }),
    z.object({
      kind: z.literal("string"),
      default: z.string().optional(),
      multiline: z.boolean().optional(),
    }),
    z.object({
      kind: z.literal("enum"),
      options: z.array(z.string()).min(1),
      default: z.string().optional(),
    }),
    z.object({
      kind: z.literal("vec2"),
      default: z.tuple([z.number(), z.number()]).optional(),
    }),
    z.object({ kind: z.literal("color"), default: z.string().optional() }),
    z.object({ kind: z.literal("asset-ref") }),
    z.object({ kind: z.literal("entity-ref") }),
    z.object({
      kind: z.literal("range"),
      min: z.number(),
      max: z.number(),
      default: z.number().optional(),
    }),
    z.object({ kind: z.literal("list"), of: PropType }),
  ]).superRefine((val, ctx) => {
    // Cross-Field-Semantik (geht nicht je Member, da z.discriminatedUnion rohe ZodObjects braucht).
    if (val.kind === "color" && val.default !== undefined && !/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(val.default)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "color.default muss #rrggbb(aa) sein", path: ["default"] });
    }
    if (val.kind === "range") {
      if (val.min > val.max) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "range.min muss <= range.max sein", path: ["min"] });
      }
      if (val.default !== undefined && (val.default < val.min || val.default > val.max)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "range.default muss in [min,max] liegen", path: ["default"] });
      }
    }
    if (val.kind === "enum" && val.default !== undefined && !val.options.includes(val.default)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "enum.default muss in options enthalten sein", path: ["default"] });
    }
  }),
);

/** Eine benannte Prop-Spezifikation (Typ + UI-Beschriftung). */
export const PropSpec = z.object({
  type: PropType,
  label: z.string().optional(),
  description: z.string().optional(),
});
export type PropSpec = z.infer<typeof PropSpec>;
